import { defaultLanguage, languages } from "../../i18n"
import { socialProfiles, shareImage } from "../../site-constants"

// Helpers for building absolute URLs, hreflang alternates, and JSON-LD.
// Used by <SEO> when a page opts in with `canonicalPath`, and by pages that
// pass `jsonLd`.

// Logo used in Organization JSON-LD. Google recommends at least 112x112px.
const ORGANIZATION_LOGO_PATH = "/assets/images/logo-with-text-vertical.png"

/**
 * Prefix an unlocalized path with the language segment (none for the default
 * language) and normalize it to a leading and trailing slash, matching how
 * Gatsby serves pages.
 */
export const localizePath = (path = "/", language = defaultLanguage) => {
  let normalized = path.startsWith("/") ? path : `/${path}`
  if (!normalized.endsWith("/")) normalized += "/"
  return language === defaultLanguage ? normalized : `/${language}${normalized}`
}

export const absoluteUrl = (siteUrl, path = "/") => `${siteUrl.replace(/\/$/, "")}${path}`

// "en-US" -> "en_US" (Open Graph locale format)
export const toOgLocale = (language) => language.replace("-", "_")

/**
 * <link rel="alternate" hreflang> entries for every built language the page
 * exists in (default: all of them), plus x-default. Empty when the page is
 * available in only one language.
 */
export const buildAlternateLinks = (siteUrl, path, availableLanguages = languages) => {
  if (availableLanguages.length < 2) return []
  return [
    ...availableLanguages.map((language) => ({
      rel: "alternate",
      hrefLang: language,
      href: absoluteUrl(siteUrl, localizePath(path, language)),
    })),
    {
      rel: "alternate",
      hrefLang: "x-default",
      href: absoluteUrl(siteUrl, localizePath(path, availableLanguages.includes(defaultLanguage) ? defaultLanguage : availableLanguages[0])),
    },
  ]
}

/**
 * Languages (from the built set) that a page exists in, given the CMS nodes
 * for its slug. A node without a locale is in the default language.
 */
export const getAvailableLanguages = (nodes = []) =>
  languages.filter((language) => nodes.some((node) => (node.locale || defaultLanguage) === language))

/**
 * imgix URL cropped to the standard link-preview size (1200x630)
 * @param {string} imgixUrl
 * @param {boolean} [cropFaces=false] center the crop on detected faces
 */
export const bannerPreviewUrl = (imgixUrl, cropFaces = false) =>
  imgixUrl
    ? `${imgixUrl}?fit=crop${cropFaces ? "&crop=faces" : ""}&w=${shareImage.width}&h=${shareImage.height}&fm=jpg&q=85`
    : undefined

const HTML_ENTITIES = { nbsp: " ", amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", rsquo: "\u2019", lsquo: "\u2018", rdquo: "\u201d", ldquo: "\u201c", ndash: "\u2013", mdash: "\u2014", hellip: "\u2026" }

/**
 * Plain-text snippet of CMS HTML for meta descriptions, cut at a word boundary.
 * Regex-based (no DOM) so it works during the Gatsby build. Returns "" if html
 * is empty.
 */
export const htmlToDescription = (html, maxLength = 160) => {
  if (!html) return ""
  const text = html
    .replace(/<\/(p|div|li|h[1-6]|ul|ol)>|<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
    .replace(/&([a-z]+);/gi, (match, name) => HTML_ENTITIES[name.toLowerCase()] ?? match)
    .replace(/\s+/g, " ")
    .trim()
  if (!text) return ""
  if (text.length <= maxLength) return text
  const cut = text.slice(0, maxLength - 1)
  return `${cut.slice(0, cut.lastIndexOf(" ") > 0 ? cut.lastIndexOf(" ") : cut.length).replace(/[\s,;:.\-]+$/, "")}\u2026`
}

export const organizationJsonLd = (siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": absoluteUrl(siteUrl, "/#organization"),
  name: "Wingspans",
  url: absoluteUrl(siteUrl, "/"),
  logo: absoluteUrl(siteUrl, ORGANIZATION_LOGO_PATH),
  sameAs: Object.values(socialProfiles),
})

export const websiteJsonLd = (siteUrl, language = defaultLanguage) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": absoluteUrl(siteUrl, "/#website"),
  name: "Wingspans",
  url: absoluteUrl(siteUrl, localizePath("/", language)),
  inLanguage: language,
  publisher: { "@id": absoluteUrl(siteUrl, "/#organization") },
})

/**
 * BreadcrumbList JSON-LD. `items` are `{ name, path }` objects ordered from the
 * top of the hierarchy down to the current page. Each path is unlocalized.
 * Pass the result to <SEO jsonLd>, alone or in an array with other schemas.
 * @param {string} siteUrl
 * @param {{ name: string, path: string }[]} items
 * @param {string} [language]
 */
export const breadcrumbJsonLd = (siteUrl, items = [], language = defaultLanguage) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(({ name, path }, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name,
    item: absoluteUrl(siteUrl, localizePath(path, language)),
  })),
})
